import { ChevronRight, FileText } from "lucide-react";
import { Link, useNavigate } from "react-router";

import { CabeceraDelMantenedor } from "@/components/plantillas/CabeceraDelMantenedor";
import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { useFormatosDeSalida } from "@/hooks/useFormatosDeSalida";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";

const FILAS_SKELETON = 4;

/**
 * Formatos de salida (listado, solo lectura).
 *
 * El formato define cómo se presenta el documento final: orden de capítulos y
 * rasgos. Los contenidos los define la plantilla; el formato solo agrega sus
 * ítems propios. Hoy es contexto para la futura generación con IA, así que no
 * se crea ni se edita nada acá.
 */
export function FormatosDeSalida() {
  const formatos = useFormatosDeSalida();
  const estado = estadoDeConsulta(formatos);
  const navigate = useNavigate();

  const lista = formatos.data ?? [];

  return (
    <div className="px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <CabeceraDelMantenedor vista="formatos" />

      <p className="text-ink-body mb-5 max-w-[40rem] text-sm font-medium text-pretty">
        El formato define la presentación y el orden de capítulos. Los contenidos los define la
        plantilla; el formato agrega sus ítems propios.
      </p>

      <div className="space-y-5">
        {estado.sinConexion && <EstadoSinConexion que="los formatos de salida" />}

        {estado.hayError && (
          <EstadoDeError
            titulo="No se pudieron cargar los formatos de salida"
            error={formatos.error}
            onReintentar={formatos.refetch}
            reintentando={formatos.isFetching}
          />
        )}

        {!estado.cargando && !estado.hayError && !estado.sinConexion && lista.length === 0 ? (
          <Empty className="rounded-xl border">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FileText />
              </EmptyMedia>
              <EmptyTitle>Todavía no hay formatos de salida</EmptyTitle>
              <EmptyDescription>Se cargan desde la planilla de estructuras de informe.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : null}

        {estado.cargando || lista.length > 0 ? (
          <div className="bg-card overflow-hidden rounded-xl border">
            <Table>
              <TableBody>
                {estado.cargando
                  ? Array.from({ length: FILAS_SKELETON }).map((_, i) => (
                      <TableRow key={i} aria-label="Cargando formato" className="hover:bg-transparent">
                        <TableCell className="px-5.5 py-4">
                          <Skeleton className="h-3.5 w-56" />
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    ))
                  : lista.map((formato) => (
                      <TableRow
                        key={formato.id}
                        className="hover:bg-superficie-sutil cursor-pointer"
                        onClick={() => navigate(`/admin/contenidos/formatos/${formato.id}`)}
                      >
                        <TableCell className="w-full max-w-0 px-5.5 py-4 whitespace-normal">
                          <Link
                            to={`/admin/contenidos/formatos/${formato.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-ink text-[0.9375rem] font-bold hover:underline"
                          >
                            {formato.nombre}
                          </Link>
                          {formato.clientes ? (
                            <p className="text-ink-hint mt-1 text-[0.78125rem] font-medium">
                              {formato.clientes}
                            </p>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-ink-hint text-xs font-semibold whitespace-nowrap">
                          <span className="text-ink font-mono">{formato.plantillas_count}</span>{" "}
                          {formato.plantillas_count === 1 ? "plantilla" : "plantillas"}
                        </TableCell>
                        <TableCell className="text-ink-ghost pr-5">
                          <ChevronRight className="h-4 w-4" aria-hidden="true" />
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </div>
        ) : null}
      </div>
    </div>
  );
}
